import { useTranslation } from "react-i18next"
import {
  Box,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material"
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight"

import type { Theme } from "@mui/material"
import type { KeyboardEvent, ReactNode } from "react"

export interface SimpleTableColumn<T> {
  /** Identifies the column, and keys its cells. */
  key: string
  labelKey: string
  /** A CSS width, e.g. "35%". Left out, the column takes what it needs. */
  width?: string
  render: (row: T) => ReactNode
}

interface SimpleTableProps<T> {
  columns: SimpleTableColumn<T>[]
  rows: T[]
  getRowKey: (row: T, index: number) => string | number
  /** Skeleton rows to stand in while the real ones load. */
  loadingRows?: number
  /** Given, the rows become clickable and grow a chevron to say so. */
  onRowClick?: (row: T) => void
}

const chevron = (
  <KeyboardArrowRightIcon
    fontSize="small"
    sx={{ display: "block", color: "text.disabled" }}
  />
)

/**
 * A plain read-only table. The board's other tables are DataGridTable, which
 * brings a toolbar, search, sorting and pagination along with it. This is for
 * the short lists that want none of that.
 *
 * Narrow screens get the same data as a block per row, since a table of any
 * width there can only squash or scroll away from the reader.
 */
const SimpleTable = <T,>({
  columns,
  rows,
  getRowKey,
  loadingRows = 0,
  onRowClick,
}: SimpleTableProps<T>) => {
  const { t } = useTranslation()
  // One shape or the other, never both: hiding one with CSS would leave its
  // content in the page twice, for a screen reader to read out twice.
  const isWide = useMediaQuery((theme: Theme) => theme.breakpoints.up("md"), {
    defaultMatches: true,
  })

  const clickProps = (row: T) =>
    onRowClick && {
      onClick: () => onRowClick(row),
      // Neither a row nor a box is focusable on its own, so let the keyboard in.
      tabIndex: 0,
      onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault()
          onRowClick(row)
        }
      },
    }

  if (isWide) {
    return (
      <TableContainer>
        <Table
          size="small"
          sx={(theme) => ({
            "& .MuiTableCell-root": {
              borderColor: theme.palette.neutral[300],
              py: 1.5,
            },
            "& .MuiTableCell-head": { ...theme.typography.overlineDense },
            "& .MuiTableRow-root:last-child .MuiTableCell-body": { border: 0 },
          })}
        >
          <TableHead>
            <TableRow>
              {columns.map(({ key, labelKey, width }) => (
                <TableCell key={key} sx={{ width }}>
                  {t(labelKey)}
                </TableCell>
              ))}
              {onRowClick && <TableCell sx={{ width: "1%" }} />}
            </TableRow>
          </TableHead>
          <TableBody>
            {loadingRows > 0
              ? Array.from({ length: loadingRows }, (_, index) => (
                  <TableRow key={index}>
                    {columns.map(({ key }) => (
                      <TableCell key={key}>
                        <Skeleton />
                      </TableCell>
                    ))}
                    {onRowClick && <TableCell />}
                  </TableRow>
                ))
              : rows.map((row, index) => (
                  <TableRow
                    key={getRowKey(row, index)}
                    hover={Boolean(onRowClick)}
                    sx={onRowClick && { cursor: "pointer" }}
                    {...clickProps(row)}
                  >
                    {columns.map(({ key, render }) => (
                      <TableCell key={key}>{render(row)}</TableCell>
                    ))}
                    {onRowClick && <TableCell>{chevron}</TableCell>}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  return (
    <Stack spacing={1}>
      {loadingRows > 0
        ? Array.from({ length: loadingRows }, (_, index) => (
            <Skeleton key={index} variant="rounded" height={120} />
          ))
        : rows.map((row, index) => (
            <Box
              key={getRowKey(row, index)}
              sx={{
                p: 1.5,
                borderRadius: 1,
                border: (theme) => `1px solid ${theme.palette.neutral[300]}`,
                ...(onRowClick && { cursor: "pointer" }),
              }}
              {...clickProps(row)}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                  {columns.map(({ key, labelKey, render }) => (
                    <Stack
                      key={key}
                      direction="row"
                      justifyContent="space-between"
                      spacing={2}
                    >
                      <Typography variant="overlineDense">{t(labelKey)}</Typography>
                      <Box sx={{ textAlign: "right" }}>{render(row)}</Box>
                    </Stack>
                  ))}
                </Stack>
                {onRowClick && chevron}
              </Stack>
            </Box>
          ))}
    </Stack>
  )
}

export default SimpleTable
